import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { __RouterContext as RouterContext } from 'react-router';
import { useHistory } from 'react-router-dom';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';

import { mobileBottomNavStyles } from '../../../../DefaultLayout.styles';
import { computeRootMatch } from '../../../MobileTabsProvider/MobileTabsProvider.utils';

import { SCROLL_RESTORE_DELAYS } from './KeepAlivePane.constants';
import { getPaneStyles } from './KeepAlivePane.styles';
import { getPrimaryScroller } from './KeepAlivePane.utils';

/**
 * One bottom-nav tab's kept-alive pane. It overrides react-router v5's router context with this
 * pane's location (frozen while the tab is inactive, live while active) so the inner `<Switch>`
 * keeps matching the tab's last route and its subtree stays mounted — route position survives a tab
 * switch. `history` stays the shared browser history, so navigation from inside the pane still drives
 * the single global history. Inactive panes are `display: none` (no scrim, no unmount); the
 * always-mounted bottom nav remains visible and tappable beside them.
 *
 * `display: none` zeroes a scroll container's `scrollTop`, so the primary scroller's position is
 * snapshotted in the render that hides the pane (DOM still laid out at that point) and re-applied to
 * the pane's primary scroller across a few frames after it is shown again (the page may rebuild that
 * node when it re-renders on reactivation, so restore is by value, not by node identity).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - the route tree (a `renderRoutes` Switch)
 * @param {boolean} props.isActive
 * @param {Object} props.location - this pane's frozen/live location
 * @param {boolean} [props.reserveBottom] - reserve space for the floating bottom nav
 */
const KeepAlivePane = ({ children, isActive, location, reserveBottom = false }) => {
  const history = useHistory();

  const containerRef = useRef(null);
  const frozenScrollTopRef = useRef(0);
  const wasActiveRef = useRef(isActive);

  // Render-phase snapshot: when this pane is transitioning active -> inactive, the DOM still reflects
  // the previous (visible) commit, so the primary scroller's live scrollTop is readable here — before
  // the `display: none` from this render is committed and zeroes it.
  if (wasActiveRef.current && !isActive) {
    const primary = getPrimaryScroller(containerRef.current);

    if (primary) {
      frozenScrollTopRef.current = primary.scrollTop;
    }
  }

  wasActiveRef.current = isActive;

  const routerValue = useMemo(
    () => ({ history, location, match: computeRootMatch(location.pathname) }),
    [history, location],
  );

  const paneSx = useMemo(
    () => [getPaneStyles({ isActive }), reserveBottom && mobileBottomNavStyles],
    [isActive, reserveBottom],
  );

  useLayoutEffect(() => {
    if (!isActive || frozenScrollTopRef.current <= 0) {
      return undefined;
    }

    const restore = () => {
      const primary = getPrimaryScroller(containerRef.current);

      if (primary && primary.scrollTop !== frozenScrollTopRef.current) {
        primary.scrollTop = frozenScrollTopRef.current;
      }
    };

    const timers = SCROLL_RESTORE_DELAYS.map(delay => setTimeout(restore, delay));

    return () => timers.forEach(clearTimeout);
  }, [isActive]);

  return (
    <Box aria-hidden={!isActive} ref={containerRef} sx={paneSx}>
      <RouterContext.Provider value={routerValue}>{children}</RouterContext.Provider>
    </Box>
  );
};

KeepAlivePane.propTypes = {
  isActive: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  children: PropTypes.node,
  reserveBottom: PropTypes.bool,
};

export default KeepAlivePane;
